/**
 * ChainVault WP - Etherscan API Utility
 *
 * Fetches real transaction history from Etherscan API V2.
 * Supports Ethereum, BNB Smart Chain, Polygon and Base.
 *
 * @package ChainVault
 * @since   1.0.0
 */

/**
 * Etherscan V2 unified API endpoint
 * One endpoint for all networks using chainid parameter
 */
const API_BASE = 'https://api.etherscan.io/v2/api';

/**
 * Chain IDs for each network — mainnet and testnet
 */
const CHAIN_IDS = {
    ethereum: 1,
    bsc:      56,
    polygon:  137,
    base:     8453,
};

const TESTNET_CHAIN_IDS = {
    ethereum: 11155111,
    bsc:      97,
    polygon:  80002,
    base:     84532,
};

const getChainId = ( network ) => {
    const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
    return isTestnet ? ( TESTNET_CHAIN_IDS[ network ] || CHAIN_IDS[ network ] ) : CHAIN_IDS[ network ];
};

/**
 * Etherscan V2 API key — set in WordPress Admin → ChainVault → Advanced
 */
const API_KEY = window.chainvaultSettings?.etherscanApiKey || 'WRMEGG7PIFN15NHFX4NKJN9PA1MXUVPIM3';

/**
 * Fetch transaction history for a wallet address
 * Returns the last 20 transactions on the selected network
 *
 * @param {string} address        Wallet address to fetch transactions for
 * @param {string} network        Network key (ethereum, bsc, polygon, base)
 * @param {number} page           Page number for pagination
 * @param {number} offset         Number of transactions per page
 * @return {Promise<Array>}       Array of transaction objects
 */
export async function fetchTransactions(
    address,
    network = 'ethereum',
    page = 1,
    offset = 20
) {
    const chainId = getChainId( network );

    if ( ! chainId ) {
        console.error( `No chain ID for network: ${ network }` );
        return [];
    }

    try {
        const url = new URL( API_BASE );
        url.searchParams.set( 'chainid', chainId );
        url.searchParams.set( 'module', 'account' );
        url.searchParams.set( 'action', 'txlist' );
        url.searchParams.set( 'address', address );
        url.searchParams.set( 'startblock', '0' );
        url.searchParams.set( 'endblock', '99999999' );
        url.searchParams.set( 'page', page );
        url.searchParams.set( 'offset', offset );
        url.searchParams.set( 'sort', 'desc' );
        url.searchParams.set( 'apikey', API_KEY );

        const response = await fetch( url.toString() );
        const data = await response.json();

        if ( data.status === '1' && Array.isArray( data.result ) ) {
            return data.result.map( tx => ( {
                hash:      tx.hash,
                from:      tx.from,
                to:        tx.to,
                value:     parseFloat( tx.value ) / 1e18,
                timestamp: parseInt( tx.timeStamp ),
                gasUsed:   tx.gasUsed,
                gasPrice:  tx.gasPrice,
                isError:   tx.isError === '1',
                network,
            } ) );
        }

        return [];
    } catch ( error ) {
        console.error( 'Failed to fetch transactions:', error );
        return [];
    }
}

/**
 * Fetch ERC20 token transfer history
 * Returns the last 20 token transfers for a wallet address
 *
 * @param {string} address        Wallet address
 * @param {string} network        Network key
 * @return {Promise<Array>}       Array of token transfer objects
 */
export async function fetchTokenTransfers( address, network = 'ethereum' ) {
    const chainId = getChainId( network );

    if ( ! chainId ) return [];

    try {
        const url = new URL( API_BASE );
        url.searchParams.set( 'chainid', chainId );
        url.searchParams.set( 'module', 'account' );
        url.searchParams.set( 'action', 'tokentx' );
        url.searchParams.set( 'address', address );
        url.searchParams.set( 'page', '1' );
        url.searchParams.set( 'offset', '20' );
        url.searchParams.set( 'sort', 'desc' );
        url.searchParams.set( 'apikey', API_KEY );

        const response = await fetch( url.toString() );
        const data = await response.json();

        if ( data.status === '1' && Array.isArray( data.result ) ) {
            return data.result.map( tx => ( {
                hash:        tx.hash,
                from:        tx.from,
                to:          tx.to,
                value:       parseFloat( tx.value ) / Math.pow( 10, parseInt( tx.tokenDecimal ) ),
                timestamp:   parseInt( tx.timeStamp ),
                tokenName:   tx.tokenName,
                tokenSymbol: tx.tokenSymbol,
                network,
            } ) );
        }

        return [];
    } catch ( error ) {
        console.error( 'Failed to fetch token transfers:', error );
        return [];
    }
}

/**
 * Format a Unix timestamp to a readable date
 *
 * @param {number} timestamp Unix timestamp in seconds
 * @return {string} Formatted date string like "Apr 20, 2026"
 */
export function formatTimestamp( timestamp ) {
    const date = new Date( timestamp * 1000 );
    return date.toLocaleDateString( 'en-US', {
        month: 'short',
        day:   'numeric',
        year:  'numeric',
    } );
}

/**
 * Shorten a transaction hash for display
 *
 * @param {string} hash Full transaction hash
 * @return {string} Shortened hash like 0x1234...5678
 */
export function shortenHash( hash ) {
    if ( ! hash ) return '';
    return `${ hash.slice( 0, 8 ) }...${ hash.slice( -6 ) }`;
}